import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DRAWER_CARD_CLASS, DRAWER_CARD_TITLE_CLASS } from '../shared/styles'
import Tooltip from '../../ui/Tooltip'
import InfoTooltip from '../../ui/InfoTooltip'
import Banner from '../../ui/Banner'
import DownloadOutlineIcon from '../../ui/icons/DownloadOutlineIcon'
import EyeIcon from '../../ui/icons/EyeIcon'
import ImageIcon from '../../ui/icons/ImageIcon'
import TrashCanIcon from '../../ui/icons/TrashCanIcon'
import { formatDateTime } from '../../../utils/dateHelpers'
import ConfirmRemoveFileModal from './ConfirmRemoveFileModal'
import PhotoViewer, { buildViewerPhotos, usePhotoViewer } from '../../ui/PhotoViewer'

const THUMBNAIL_URL_KEY = 'thumbnail_url'
const DOWNLOAD_URL_KEY = 'download_url'
const SIGNED_ID_KEY = 'signed_id'
const UPLOADED_BY_KEY = 'uploaded_by'
const UPLOADED_AT_KEY = 'uploaded_at'

const ACCEPT = 'image/png,image/jpeg,image/bmp'
const MAX_BYTES = 10 * 1024 * 1024
const SIZE_ERROR = 'Le fichier dépasse 10 MB. Veuillez importer une image de moins de 10 MB.'
const INFO_TEXT = 'Format autorisé : PNG, JPEG (poids max : 10 MB)'
const EMPTY_TEXT = 'Aucun fichier importé.'
const PHOTO_ALT = 'Photo de l’alerte'

const CARD_CLASS = `${DRAWER_CARD_CLASS} tw:gap-4`
const HEAD_CLASS = 'tw:flex tw:items-center tw:justify-between tw:gap-2'
const TITLE_CLASS = `${DRAWER_CARD_TITLE_CLASS} tw:flex tw:items-center tw:gap-2`
const ADD_BUTTON_CLASS = 'tw:inline-flex tw:items-center tw:gap-2 tw:h-9 tw:px-3 tw:rounded! '
  + 'tw:border tw:border-solid tw:border-gray-300 tw:bg-white tw:cursor-pointer '
  + 'tw:text-sm tw:text-slate-700 tw:hover:bg-gray-100'
const ROW_CLASS = 'tw:flex tw:items-center tw:gap-3 tw:rounded-lg! tw:border tw:border-solid '
  + 'tw:border-gray-200 tw:bg-white tw:pr-3'
const THUMB_CLASS = 'tw:size-14 tw:object-cover tw:rounded! tw:border tw:border-solid tw:border-gray-200'
const MISSING_THUMB_CLASS = 'tw:flex tw:items-center tw:justify-center tw:size-14 tw:shrink-0 tw:rounded! '
  + 'tw:border tw:border-solid tw:border-gray-200 tw:bg-gray-50 tw:text-gray-400 tw:text-xl!'
const ROW_TEXT_CLASS = 'tw:flex tw:flex-col tw:min-w-0 tw:flex-1'
const ROW_TITLE_CLASS = 'tw:text-sm tw:text-gray-900 tw:truncate'
const ROW_META_CLASS = 'tw:text-sm tw:text-gray-500'
const ACTIONS_CLASS = 'tw:flex tw:items-center tw:gap-1 tw:shrink-0'
const ACTION_BASE_CLASS = 'tw:inline-flex tw:items-center tw:justify-center tw:size-9 tw:shrink-0 tw:p-2 '
  + 'tw:rounded! tw:bg-transparent tw:border-0 tw:cursor-pointer tw:no-underline! tw:text-xl!'
const ACTION_CLASS = `${ACTION_BASE_CLASS} tw:text-gray-500 tw:hover:bg-gray-100`
const DELETE_CLASS = `${ACTION_BASE_CLASS} tw:text-red-500 tw:hover:bg-red-100`
// TrashCanIcon darkens itself on hover; Figma keeps red/500, so the hover belongs to the button only.
const TRASH_ICON_CLASS = 'tw:size-5 tw:pointer-events-none'
const EMPTY_CLASS = 'tw:text-sm tw:text-gray-500 tw:m-0!'
const HIDDEN_INPUT_CLASS = 'tw:hidden'

const EMPTY_LIST = []
const EMPTY_IDS = new Set()
const DOWNLOAD_FRAME_LIFETIME_MS = 60000

// A hidden frame keeps the drawer, and any edit in progress, when the stored file is missing.
const downloadInBackground = url => {
  const frame = document.createElement('iframe')
  frame.hidden = true
  frame.src = url
  document.body.appendChild(frame)
  setTimeout(() => frame.remove(), DOWNLOAD_FRAME_LIFETIME_MS)
}

const newFileKey = file => `${file.name}-${file.size}-${file.lastModified}`

const uploadLabel = photo => {
  const author = photo[UPLOADED_BY_KEY]
  return author ? `Importé par ${author}` : 'Fichier importé'
}

const IssueFilesCard = ({
  photos = EMPTY_LIST, newPhotos = EMPTY_LIST, removedPhotoIds = EMPTY_LIST,
  onAddPhotos, onRemoveExisting, onRemoveNew
}) => {
  const inputRef = useRef(null)
  const [sizeError, setSizeError] = useState(null)
  const [previewUrls, setPreviewUrls] = useState(EMPTY_LIST)
  const [fileToRemove, setFileToRemove] = useState(null)
  const [missingThumbIds, setMissingThumbIds] = useState(EMPTY_IDS)
  const { handleThumbClick, viewerProps } = usePhotoViewer()

  useEffect(() => {
    const urls = newPhotos.map(file => URL.createObjectURL(file))
    setPreviewUrls(urls)
    return () => urls.forEach(url => URL.revokeObjectURL(url))
  }, [newPhotos])

  const handlePick = useCallback(() => inputRef.current?.click(), [])

  const handleChange = useCallback(event => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    const tooLarge = files.filter(file => file.size > MAX_BYTES)
    setSizeError(tooLarge.length > 0 ? SIZE_ERROR : null)
    const accepted = files.filter(file => file.size <= MAX_BYTES)
    if (accepted.length > 0)
      onAddPhotos(accepted)
  }, [onAddPhotos])

  // The bin only stages a removal, but the drawer's sole undo discards every other edit too.
  const handleRemoveExisting = useCallback(event => {
    setFileToRemove(event.currentTarget.dataset.signedId)
  }, [])

  const cancelRemoveExisting = useCallback(() => setFileToRemove(null), [])

  const confirmRemoveExisting = useCallback(() => {
    onRemoveExisting(fileToRemove)
    setFileToRemove(null)
  }, [onRemoveExisting, fileToRemove])

  const handleDownload = useCallback(event => {
    downloadInBackground(event.currentTarget.dataset.downloadUrl)
  }, [])

  const handleThumbError = useCallback(event => {
    const photoId = Number(event.currentTarget.dataset.photoId)
    setMissingThumbIds(previous => new Set(previous).add(photoId))
  }, [])

  const handleRemoveNew = useCallback(event => {
    onRemoveNew(Number(event.currentTarget.dataset.index))
  }, [onRemoveNew])

  const visible = photos.filter(photo => !removedPhotoIds.includes(photo[SIGNED_ID_KEY]))

  const renderThumb = photo => {
    if (missingThumbIds.has(photo.id))
      return <span className={MISSING_THUMB_CLASS}><ImageIcon /></span>
    return (
      <img
        alt=""
        className={THUMB_CLASS}
        data-photo-id={photo.id}
        src={photo[THUMBNAIL_URL_KEY]}
        onError={handleThumbError}
      />
    )
  }

  const renderExisting = (photo, index) => (
    <div key={photo.id} className={ROW_CLASS}>
      {renderThumb(photo)}
      <span className={ROW_TEXT_CLASS}>
        <span className={ROW_TITLE_CLASS}>{uploadLabel(photo)}</span>
        <span className={ROW_META_CLASS}>{`Le ${formatDateTime(photo[UPLOADED_AT_KEY])}`}</span>
        {missingThumbIds.has(photo.id) ? <span className={ROW_META_CLASS}>Fichier indisponible</span> : null}
      </span>
      <span className={ACTIONS_CLASS}>
        <Tooltip className="tw:inline-flex" content="Télécharger">
          <button
            aria-label="Télécharger le fichier"
            className={ACTION_CLASS}
            data-download-url={photo[DOWNLOAD_URL_KEY]}
            type="button"
            onClick={handleDownload}
          >
            <DownloadOutlineIcon />
          </button>
        </Tooltip>
        <Tooltip className="tw:inline-flex" content="Voir">
          <button
            aria-label="Voir le fichier"
            className={ACTION_CLASS}
            data-viewer-index={index}
            type="button"
            onClick={handleThumbClick}
          >
            <EyeIcon />
          </button>
        </Tooltip>
        <Tooltip className="tw:inline-flex" content="Supprimer">
          <button
            aria-label="Supprimer le fichier"
            className={DELETE_CLASS}
            data-signed-id={photo[SIGNED_ID_KEY]}
            type="button"
            onClick={handleRemoveExisting}
          >
            <TrashCanIcon className={TRASH_ICON_CLASS} />
          </button>
        </Tooltip>
      </span>
    </div>
  )

  const renderNew = (file, index) => (
    <div key={newFileKey(file)} className={ROW_CLASS}>
      <img alt="" className={THUMB_CLASS} src={previewUrls[index]} />
      <span className={ROW_TEXT_CLASS}>
        <span className={ROW_TITLE_CLASS}>{file.name}</span>
        <span className={ROW_META_CLASS}>En attente de sauvegarde</span>
      </span>
      <span className={ACTIONS_CLASS}>
        <Tooltip className="tw:inline-flex" content="Retirer">
          <button
            aria-label="Retirer le fichier"
            className={DELETE_CLASS}
            data-index={index}
            type="button"
            onClick={handleRemoveNew}
          >
            <TrashCanIcon className={TRASH_ICON_CLASS} />
          </button>
        </Tooltip>
      </span>
    </div>
  )

  const isEmpty = visible.length === 0 && newPhotos.length === 0

  return (
    <section className={CARD_CLASS}>
      <ConfirmRemoveFileModal
        isOpen={fileToRemove !== null}
        onCancel={cancelRemoveExisting}
        onConfirm={confirmRemoveExisting}
      />
      <div className={HEAD_CLASS}>
        <h3 className={TITLE_CLASS}>
          <span>Fichiers</span>
          <InfoTooltip content={INFO_TEXT} />
        </h3>
        <button className={ADD_BUTTON_CLASS} type="button" onClick={handlePick}>
          <i aria-hidden="true" className="fas fa-plus" />
          <span>Ajouter un fichier</span>
        </button>
      </div>
      <input
        accept={ACCEPT}
        className={HIDDEN_INPUT_CLASS}
        ref={inputRef}
        type="file"
        multiple
        onChange={handleChange}
      />
      {sizeError ? <Banner variant="danger">{sizeError}</Banner> : null}
      {isEmpty ? <p className={EMPTY_CLASS}>{EMPTY_TEXT}</p> : null}
      {visible.map(renderExisting)}
      {newPhotos.map(renderNew)}
      <PhotoViewer photos={buildViewerPhotos(visible, PHOTO_ALT)} {...viewerProps} />
    </section>
  )
}

IssueFilesCard.propTypes = {
  newPhotos: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  onAddPhotos: PropTypes.func.isRequired,
  onRemoveExisting: PropTypes.func.isRequired,
  onRemoveNew: PropTypes.func.isRequired,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    url: PropTypes.string
  })),
  removedPhotoIds: PropTypes.arrayOf(PropTypes.string)
}

IssueFilesCard.defaultProps = {
  newPhotos: EMPTY_LIST,
  photos: EMPTY_LIST,
  removedPhotoIds: EMPTY_LIST
}

export default IssueFilesCard
